import React, { useState } from 'react';
import { Calculator, Calendar, ExternalLink, Info } from 'lucide-react';

const KnowledgeBankSection = () => {
  const [activeSubsection, setActiveSubsection] = useState('calculators');

  // GST Calculator State
  const [gstAmount, setGstAmount] = useState('');
  const [gstRate, setGstRate] = useState('18');
  const [gstResult, setGstResult] = useState(null);

  // EMI Calculator State
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [tenure, setTenure] = useState('');
  const [emiResult, setEmiResult] = useState(null);

  // Income Tax Calculator State
  const [income, setIncome] = useState('');
  const [taxRegime, setTaxRegime] = useState('new');
  const [taxResult, setTaxResult] = useState(null);

  const calculateGST = () => {
    const amount = parseFloat(gstAmount);
    const rate = parseFloat(gstRate);
    if (!isNaN(amount) && !isNaN(rate)) {
      const gst = (amount * rate) / 100;
      const total = amount + gst;
      setGstResult({ amount, gst, total });
    }
  };

  const calculateEMI = () => {
    const p = parseFloat(principal);
    const r = parseFloat(rate) / 12 / 100;
    const n = parseFloat(tenure) * 12;
    
    if (!isNaN(p) && !isNaN(r) && !isNaN(n) && r > 0 && n > 0) {
      const emi = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      const totalAmount = emi * n;
      const totalInterest = totalAmount - p;
      setEmiResult({ emi, totalAmount, totalInterest });
    }
  };

  const calculateIncomeTax = () => {
    const inc = parseFloat(income);
    if (isNaN(inc)) return;

    let tax = 0;
    
    if (taxRegime === 'old') {
      // Old Regime FY 2025-26 (with standard deduction)
      const taxableIncome = Math.max(0, inc - 50000); // Standard deduction
      if (taxableIncome <= 250000) tax = 0;
      else if (taxableIncome <= 500000) tax = (taxableIncome - 250000) * 0.05;
      else if (taxableIncome <= 1000000) {
        tax = 12500 + (taxableIncome - 500000) * 0.20;
      } else {
        tax = 12500 + 100000 + (taxableIncome - 1000000) * 0.30;
      }
    } else {
      // New Regime FY 2025-26
      if (inc <= 300000) tax = 0;
      else if (inc <= 700000) tax = (inc - 300000) * 0.05;
      else if (inc <= 1000000) {
        tax = 20000 + (inc - 700000) * 0.10;
      } else if (inc <= 1200000) {
        tax = 20000 + 30000 + (inc - 1000000) * 0.15;
      } else if (inc <= 1500000) {
        tax = 20000 + 30000 + 30000 + (inc - 1200000) * 0.20;
      } else {
        tax = 20000 + 30000 + 30000 + 60000 + (inc - 1500000) * 0.30;
      }
    }

    // Add 4% cess
    tax = tax * 1.04;
    const netIncome = inc - tax;
    setTaxResult({ tax, netIncome });
  };

  const subsections = [
    { id: 'calculators', label: 'Calculators', icon: Calculator },
    { id: 'compliance', label: 'Compliance Due Dates', icon: Calendar },
    { id: 'links', label: 'Useful Links', icon: ExternalLink },
    { id: 'rates', label: 'Key Reference Rates', icon: Info }
  ];

  const usefulLinks = [
    { name: 'Income Tax e-Filing Portal', url: 'https://incometax.gov.in' },
    { name: 'GST Portal', url: 'https://gst.gov.in' },
    { name: 'MCA21 Portal', url: 'https://mca.gov.in' },
    { name: 'ICAI Website', url: 'https://icai.org' },
    { name: 'RBI Website', url: 'https://rbi.org.in' },
    { name: 'SEBI Website', url: 'https://sebi.gov.in' },
    { name: 'EPF Member Portal', url: 'https://epfindia.gov.in' },
    { name: 'TRACES Portal', url: 'https://tdscpc.gov.in' }
  ];

  const complianceDates = [
    { task: 'GSTR-1 (Monthly)', due: '11th of next month', category: 'GST' },
    { task: 'GSTR-3B (Monthly)', due: '20th of next month', category: 'GST' },
    { task: 'GSTR-1 (Quarterly)', due: '13th of next month', category: 'GST' },
    { task: 'TDS Return (Q1, Q2, Q3)', due: '31st July, 31st Oct, 31st Jan', category: 'TDS' },
    { task: 'TDS Return (Q4)', due: '31st May', category: 'TDS' },
    { task: 'Advance Tax (1st Installment)', due: '15th June', category: 'Income Tax' },
    { task: 'Advance Tax (2nd Installment)', due: '15th September', category: 'Income Tax' },
    { task: 'Advance Tax (3rd Installment)', due: '15th December', category: 'Income Tax' },
    { task: 'Advance Tax (4th Installment)', due: '15th March', category: 'Income Tax' },
    { task: 'Income Tax Return (Individuals)', due: '31st July', category: 'Income Tax' },
    { task: 'Income Tax Return (Companies)', due: '31st October', category: 'Income Tax' },
    { task: 'ROC Annual Filing (AOC-4, MGT-7)', due: 'Within 30 days of AGM', category: 'ROC' }
  ];

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D1B2A] via-[#1a2d42] to-[#0D1B2A]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-[#C9A84C] mb-4">
            Knowledge Bank
          </h2>
          <div className="w-24 h-1 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-gray-300 font-dmsans max-w-3xl mx-auto">
            Tools, resources, and reference materials for tax and compliance management.
          </p>
        </div>

        {/* Subsection Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {subsections.map((subsec) => {
            const Icon = subsec.icon;
            return (
              <button
                key={subsec.id}
                onClick={() => setActiveSubsection(subsec.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-dmsans font-semibold transition-all duration-300 ${
                  activeSubsection === subsec.id
                    ? 'bg-[#C9A84C] text-[#0D1B2A] shadow-lg'
                    : 'bg-[#1a2d42] bg-opacity-50 text-gray-300 hover:bg-[#C9A84C] hover:text-[#0D1B2A] border border-[#C9A84C] border-opacity-20'
                }`}
              >
                <Icon size={20} />
                {subsec.label}
              </button>
            );
          })}
        </div>

        {/* Calculators */}
        {activeSubsection === 'calculators' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* GST Calculator */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4">GST Calculator</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    value={gstAmount}
                    onChange={(e) => setGstAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Enter amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">GST Rate (%)</label>
                  <select
                    value={gstRate}
                    onChange={(e) => setGstRate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                  >
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>
                <button
                  onClick={calculateGST}
                  className="w-full bg-[#C9A84C] text-[#0D1B2A] py-2 rounded font-dmsans font-semibold hover:bg-[#b8973d] transition-colors"
                >
                  Calculate
                </button>
                {gstResult && (
                  <div className="mt-4 p-4 bg-[#FAF8F3] rounded text-sm font-dmsans">
                    <p>Base Amount: ₹{gstResult.amount.toFixed(2)}</p>
                    <p>GST Amount: ₹{gstResult.gst.toFixed(2)}</p>
                    <p className="font-bold text-[#0D1B2A]">Total: ₹{gstResult.total.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Income Tax Calculator */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4">Income Tax Calculator</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Annual Income (₹)</label>
                  <input
                    type="number"
                    value={income}
                    onChange={(e) => setIncome(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Enter income"
                  />
                </div>
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Tax Regime</label>
                  <select
                    value={taxRegime}
                    onChange={(e) => setTaxRegime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                  >
                    <option value="new">New Regime (FY 2025-26)</option>
                    <option value="old">Old Regime (FY 2025-26)</option>
                  </select>
                </div>
                <button
                  onClick={calculateIncomeTax}
                  className="w-full bg-[#C9A84C] text-[#0D1B2A] py-2 rounded font-dmsans font-semibold hover:bg-[#b8973d] transition-colors"
                >
                  Calculate
                </button>
                {taxResult && (
                  <div className="mt-4 p-4 bg-[#FAF8F3] rounded text-sm font-dmsans">
                    <p>Tax Payable: ₹{taxResult.tax.toFixed(2)}</p>
                    <p className="font-bold text-[#0D1B2A]">Net Income: ₹{taxResult.netIncome.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* EMI Calculator */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4">EMI Calculator</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Principal (₹)</label>
                  <input
                    type="number"
                    value={principal}
                    onChange={(e) => setPrincipal(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Loan amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Rate (% p.a.)</label>
                  <input
                    type="number"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Interest rate"
                  />
                </div>
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Tenure (years)</label>
                  <input
                    type="number"
                    value={tenure}
                    onChange={(e) => setTenure(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Loan period"
                  />
                </div>
                <button
                  onClick={calculateEMI}
                  className="w-full bg-[#C9A84C] text-[#0D1B2A] py-2 rounded font-dmsans font-semibold hover:bg-[#b8973d] transition-colors"
                >
                  Calculate
                </button>
                {emiResult && (
                  <div className="mt-4 p-4 bg-[#FAF8F3] rounded text-sm font-dmsans">
                    <p className="font-bold text-[#0D1B2A]">Monthly EMI: ₹{emiResult.emi.toFixed(2)}</p>
                    <p>Total Amount: ₹{emiResult.totalAmount.toFixed(2)}</p>
                    <p>Total Interest: ₹{emiResult.totalInterest.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Compliance Due Dates */}
        {activeSubsection === 'compliance' && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0D1B2A] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-playfair">Compliance Task</th>
                    <th className="px-6 py-4 text-left font-playfair">Due Date</th>
                    <th className="px-6 py-4 text-left font-playfair">Category</th>
                  </tr>
                </thead>
                <tbody className="font-dmsans">
                  {complianceDates.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-[#FAF8F3]' : 'bg-white'}>
                      <td className="px-6 py-4 text-gray-800">{item.task}</td>
                      <td className="px-6 py-4 text-gray-700">{item.due}</td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-[#C9A84C] text-[#0D1B2A] rounded-full text-sm font-semibold">
                          {item.category}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Useful Links */}
        {activeSubsection === 'links' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {usefulLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-t-4 border-[#C9A84C] flex items-center gap-4"
              >
                <ExternalLink className="text-[#C9A84C] flex-shrink-0" size={24} />
                <span className="font-dmsans font-semibold text-[#0D1B2A]">{link.name}</span>
              </a>
            ))}
          </div>
        )}

        {/* Key Reference Rates */}
        {activeSubsection === 'rates' && (
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4 border-b-2 border-[#C9A84C] pb-2">
                Income Tax Slabs (FY 2025-26)
              </h3>
              <div className="space-y-4 font-dmsans text-sm">
                <div>
                  <p className="font-semibold text-[#0D1B2A] mb-2">New Regime:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>₹0 - ₹3,00,000: Nil</li>
                    <li>₹3,00,001 - ₹7,00,000: 5%</li>
                    <li>₹7,00,001 - ₹10,00,000: 10%</li>
                    <li>₹10,00,001 - ₹12,00,000: 15%</li>
                    <li>₹12,00,001 - ₹15,00,000: 20%</li>
                    <li>Above ₹15,00,000: 30%</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-[#0D1B2A] mb-2">Old Regime:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>₹0 - ₹2,50,000: Nil</li>
                    <li>₹2,50,001 - ₹5,00,000: 5%</li>
                    <li>₹5,00,001 - ₹10,00,000: 20%</li>
                    <li>Above ₹10,00,000: 30%</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4 border-b-2 border-[#C9A84C] pb-2">
                GST Rates Overview
              </h3>
              <ul className="space-y-2 font-dmsans text-sm text-gray-700">
                <li className="flex justify-between">
                  <span>Essential goods (food grains, etc.)</span>
                  <span className="font-semibold">0%</span>
                </li>
                <li className="flex justify-between">
                  <span>Necessities (medicines, etc.)</span>
                  <span className="font-semibold">5%</span>
                </li>
                <li className="flex justify-between">
                  <span>Standard goods</span>
                  <span className="font-semibold">12%</span>
                </li>
                <li className="flex justify-between">
                  <span>Most goods & services</span>
                  <span className="font-semibold">18%</span>
                </li>
                <li className="flex justify-between">
                  <span>Luxury items (cars, etc.)</span>
                  <span className="font-semibold">28%</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4 border-b-2 border-[#C9A84C] pb-2">
                TDS Rate Chart
              </h3>
              <ul className="space-y-2 font-dmsans text-sm text-gray-700">
                <li className="flex justify-between">
                  <span>194A - Interest (Bank/Company)</span>
                  <span className="font-semibold">10%</span>
                </li>
                <li className="flex justify-between">
                  <span>194C - Contractor Payments</span>
                  <span className="font-semibold">1-2%</span>
                </li>
                <li className="flex justify-between">
                  <span>194H - Commission/Brokerage</span>
                  <span className="font-semibold">5%</span>
                </li>
                <li className="flex justify-between">
                  <span>194I - Rent (Plant & Machinery)</span>
                  <span className="font-semibold">2%</span>
                </li>
                <li className="flex justify-between">
                  <span>194I - Rent (Land & Building)</span>
                  <span className="font-semibold">10%</span>
                </li>
                <li className="flex justify-between">
                  <span>194J - Professional/Technical</span>
                  <span className="font-semibold">10%</span>
                </li>
                <li className="flex justify-between">
                  <span>194Q - Purchase of Goods</span>
                  <span className="font-semibold">0.1%</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4 border-b-2 border-[#C9A84C] pb-2">
                Depreciation Rates (Companies Act)
              </h3>
              <ul className="space-y-2 font-dmsans text-sm text-gray-700">
                <li className="flex justify-between">
                  <span>Buildings</span>
                  <span className="font-semibold">5-10%</span>
                </li>
                <li className="flex justify-between">
                  <span>Plant & Machinery</span>
                  <span className="font-semibold">15%</span>
                </li>
                <li className="flex justify-between">
                  <span>Furniture & Fixtures</span>
                  <span className="font-semibold">10%</span>
                </li>
                <li className="flex justify-between">
                  <span>Computers & Software</span>
                  <span className="font-semibold">40%</span>
                </li>
                <li className="flex justify-between">
                  <span>Motor Vehicles</span>
                  <span className="font-semibold">15-30%</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default KnowledgeBankSection;
